/**
 * Receber faturas por e-mail — passos P7 e P8 do assistente de configuração
 * (out/2026).
 *
 * P7: imóvel + distribuidora, só as quatro com leitura automática (CPFL
 * Paulista, Elektro/Neoenergia, Enel SP, Light) e "Minha distribuidora não
 * aparece na lista" (pedido ao suporte com a fatura anexada). P8: endereço
 * EnergyPass com copiar, onde cadastrar e o CÓDIGO DE VERIFICAÇÃO na tela, com
 * contagem regressiva (antes só ia por e-mail e valia 5 min — o cliente perdia
 * a janela). "Pronto, cadastrei" grava `inboundSetupAt` e o imóvel passa a
 * "aguardando 1ª fatura" em Integrações. Free vê o recurso bloqueado.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import { Modal } from 'ui';
import { Button } from 'ui/button/button.ui';
import { alert } from 'lib/alert';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { currentUser } from 'store/user/user.selector';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';
import { getConsumerUnitList } from 'store/consumer_unit/consumer_unit.action';

import {
  generateAliasService,
  markInboundSetupService,
} from 'services/inbound_email.service';
import { canAutoReceiveInvoice } from 'utils/planRules';
import { getUtilityCompanyLogo } from 'utils/logo';
import { INBOUND_UTILITIES, inboundUtilityFor } from 'utils/inboundStatus';
import { useInboundToken } from 'hooks/useInboundToken';

import { CopyableValue } from 'components/setup-wizard/copyable-value.component';
import { UnlistedSetupRequest } from 'components/setup-wizard/unlisted-setup-request.component';
import {
  ChoiceCard,
  ChoiceGrid,
  Field,
  Lead,
  Notice,
  StepTag,
  UnlistedOption,
  WizardBody,
  WizardFooter,
} from 'components/setup-wizard/setup-wizard.styles';

import { CodePanel } from './inbound-email.styles';

interface InboundEmailWizardProps {
  show: boolean;
  onClose: () => void;
  defaultConsumerUnitId?: string;
  /** "Passo 2 de 3" quando aberto pelo Primeiros passos. */
  stepLabel?: string;
  /** Abre direto no pedido "distribuidora não aparece" (imóvel de envio manual). */
  initialPage?: 'utility' | 'unlisted';
}

type Page = 'utility' | 'setup' | 'unlisted';

const mmss = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export const InboundEmailWizard = ({
  show,
  onClose,
  defaultConsumerUnitId,
  stepLabel,
  initialPage = 'utility',
}: InboundEmailWizardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(currentUser);
  const consumerUnitList = useAppSelector(selectConsumerUnitList);
  const allowed = canAutoReceiveInvoice(user?.plan?.name ?? 'Free');

  const [page, setPage] = useState<Page>('utility');
  const [unitId, setUnitId] = useState('');
  const [utilitySlug, setUtilitySlug] = useState('');
  const [address, setAddress] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const token = useInboundToken(show && page === 'setup');

  useEffect(() => {
    if (!show) return;
    const uc =
      consumerUnitList.find((u) => u.id === defaultConsumerUnitId) ??
      consumerUnitList[0];
    setUnitId(uc?.id ?? '');
    setUtilitySlug(inboundUtilityFor(uc?.utilityCompany?.slug)?.slug ?? '');
    setPage(initialPage);
  }, [show]);

  useEffect(() => {
    if (!show || !allowed || page !== 'setup' || address) return;
    generateAliasService()
      .then(({ data }) => setAddress(data.data.address))
      .catch(() =>
        alert.error('Não foi possível gerar o seu endereço EnergyPass.')
      );
  }, [show, allowed, page, address]);

  const utility = inboundUtilityFor(utilitySlug);
  const unit = consumerUnitList.find((u) => u.id === unitId);

  const done = async () => {
    if (!unitId) return;
    setSaving(true);
    try {
      await markInboundSetupService(unitId);
      dispatch(getConsumerUnitList());
      alert.success(
        'Pronto! A primeira fatura chega no próximo ciclo, em até 30 dias.'
      );
      onClose();
    } catch (err) {
      alert.error(
        (err as { message?: string })?.message || 'Não foi possível salvar.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return (
      <Modal show={show} close={onClose} title="Receber faturas por e-mail">
        <WizardBody>
          <h3>Fatura no automático nos planos pagos</h3>
          <Lead>
            Nos planos pagos (Starter em diante), a fatura chega sozinha da
            distribuidora: você cadastra um endereço do EnergyPass uma única vez
            e não precisa mais baixar o PDF todo mês.
          </Lead>
          <Notice tone="info">
            <p>No plano Free, importe a conta nova em PDF todo mês.</p>
          </Notice>
        </WizardBody>
        <WizardFooter>
          <Button variant="inverse" type="button" onClick={onClose}>
            Fechar
          </Button>
          <Button
            type="button"
            onClick={() => {
              onClose();
              navigate('/conta');
            }}
          >
            Ver planos
          </Button>
        </WizardFooter>
      </Modal>
    );
  }

  return (
    <Modal show={show} close={onClose} title="Receber faturas por e-mail">
      {page === 'unlisted' ? (
        <UnlistedSetupRequest
          kind="utility"
          defaultConsumerUnitId={unitId || undefined}
          onBack={() => setPage('utility')}
          onClose={onClose}
        />
      ) : page === 'utility' ? (
        <>
          <WizardBody>
            <StepTag>{stepLabel ?? 'Fatura por e-mail'}</StepTag>
            <h3>Qual é a sua distribuidora?</h3>
            <Lead>
              Você cadastra um endereço do EnergyPass nela, uma única vez. A
              fatura chega até nós e é lida sozinha.
            </Lead>
            <Field>
              Imóvel
              <select
                value={unitId}
                onChange={(e) => {
                  setUnitId(e.target.value);
                  const uc = consumerUnitList.find(
                    (u) => u.id === e.target.value
                  );
                  setUtilitySlug(
                    inboundUtilityFor(uc?.utilityCompany?.slug)?.slug ?? ''
                  );
                }}
              >
                {consumerUnitList.map((uc) => (
                  <option key={uc.id} value={uc.id}>
                    {uc.name} (UC {uc.installation})
                  </option>
                ))}
              </select>
            </Field>
            <ChoiceGrid>
              {INBOUND_UTILITIES.map((u) => (
                <ChoiceCard
                  key={u.slug}
                  type="button"
                  selected={utilitySlug === u.slug}
                  onClick={() => setUtilitySlug(u.slug)}
                >
                  <img src={getUtilityCompanyLogo(u.slug)} alt="" />
                  <span>
                    <strong>{u.label}</strong>
                  </span>
                </ChoiceCard>
              ))}
            </ChoiceGrid>
            <UnlistedOption type="button" onClick={() => setPage('unlisted')}>
              <b>Minha distribuidora não aparece na lista</b>{' '}
              <span>— o suporte configura a partir da sua fatura</span>
            </UnlistedOption>
          </WizardBody>
          <WizardFooter>
            <Button variant="inverse" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="secondary"
              type="button"
              disabled={!utility || !unitId}
              onClick={() => setPage('setup')}
            >
              Próximo
              <BsChevronRight />
            </Button>
          </WizardFooter>
        </>
      ) : (
        <>
          <WizardBody>
            <StepTag>
              {utility?.label} · {unit?.name}
            </StepTag>
            <h3>Receber faturas por e-mail</h3>

            <p>
              <b>1. Copie o seu endereço EnergyPass</b>
            </p>
            {address ? (
              <CopyableValue
                value={address}
                copiedMessage="Endereço copiado!"
              />
            ) : (
              <Lead>Gerando o seu endereço…</Lead>
            )}

            <p style={{ marginTop: 16 }}>
              <b>2. Cadastre-o na conta digital da {utility?.label}</b>
            </p>
            <Lead>
              No site ou app da distribuidora, entre com o login do titular e
              cadastre o endereço acima para receber a conta por e-mail.{' '}
              {utility && (
                <a href={utility.site} target="_blank" rel="noreferrer">
                  {utility.siteLabel}
                </a>
              )}
            </Lead>

            <p>
              <b>3. Se a distribuidora pedir um código, ele aparece aqui</b>
            </p>
            <CodePanel>
              {token.code ? (
                <>
                  <small>
                    código recebido · expira em <b>{mmss(token.secondsLeft)}</b>
                  </small>
                  <CopyableValue
                    value={token.code}
                    label="Copiar código"
                    copiedMessage="Código copiado!"
                  />
                </>
              ) : (
                <small>aguardando o código…</small>
              )}
            </CodePanel>
            <Lead>
              O código também vai para o seu e-mail de cadastro e vale 5
              minutos. Expirou? Peça um novo no site da distribuidora.
            </Lead>
          </WizardBody>
          <WizardFooter>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setPage('utility')}
            >
              <BsChevronLeft />
              Voltar
            </Button>
            <Button type="button" onClick={done} disabled={saving || !address}>
              {saving ? 'Salvando…' : 'Pronto, cadastrei'}
            </Button>
          </WizardFooter>
        </>
      )}
    </Modal>
  );
};
